import { Autocomplete, Avatar, Box, Card, FormControlLabel, LinearProgress, Skeleton, Switch, TextField, Typography } from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUserContext } from '../../../contexts';
import { IBusinessView, IBusinessPublicView, IServiceView, IClientView, businessService, clientService } from '../../../services';
import { EntitySelector } from '../../../components';
import { ServiceCard } from '../serviceCard/ServiceCard';
import { ScheduleModal } from '../scheduleModal/ScheduleModal';
import { CreateClientModal } from '../createClientModal/CreateClientModal';

interface ICreateAppointmentFormProps {
  /** ID do estabelecimento (para pré-seleção ou bloqueio) */
  businessId?: number;
  /** ID do BusinessEmployee (para pré-seleção do profissional) */
  businessEmployeeId?: number;
  /** ID do Employee (fallback para encontrar o profissional) */
  employeeId?: number;
  /** ID do serviço (para pré-seleção, ex: reagendar) */
  serviceId?: number;
  /** Se é um encaixe (walk-in) */
  walkIn: boolean;
  /** Se veio do scheduler (mostra campo de cliente) */
  viaScheduler: boolean;
  /** Bloqueia o seletor de estabelecimento */
  blockBusiness: boolean;
  /** Bloqueia o seletor de profissional */
  blockEmployee: boolean;
  /** Callback ao criar com sucesso */
  onSuccess: () => void;
  /** Callback para expor estado do form ao wrapper (loading, canSubmit, submit) */
  onFormReady?: (api: { isLoading: boolean; canSubmit: boolean; submit: () => void }) => void;
}

export const CreateAppointmentForm: React.FC<ICreateAppointmentFormProps> = ({
  businessId: initialBusinessId,
  businessEmployeeId: initialBusinessEmployeeId,
  employeeId: initialEmployeeId,
  serviceId: initialServiceId,
  walkIn,
  viaScheduler,
  blockBusiness,
  blockEmployee,
  onSuccess,
  onFormReady,
}) => {
  const [selectedBusiness, setSelectedBusiness] = useState<IBusinessPublicView | IBusinessView>();
  const [schedulingService, setSchedulingService] = useState<IServiceView | null>(null);
  const [showUnavailable, setShowUnavailable] = useState(true);

  // Client search state (viaScheduler — antes da lista de serviços)
  const [selectedClient, setSelectedClient] = useState<IClientView | null>(null);
  const [clientSearch, setClientSearch] = useState('');
  const [clientModalOpen, setClientModalOpen] = useState(false);

  // Estado do ScheduleModal (para expor ao onFormReady)
  const [modalApi, setModalApi] = useState<{ isLoading: boolean; canSubmit: boolean; submit: () => void }>();

  const { userId } = useUserContext();

  // ── Queries ──────────────────────────────────────────────────

  const { data: businesses, isLoading: isLoadingBusinesses } = useQuery(
    ['businesses'],
    () => businessService.getPublic(),
    { enabled: !blockBusiness }
  );

  const { data: business, isLoading: isLoadingBusiness } = useQuery({
    queryKey: ['business', blockBusiness ? initialBusinessId : selectedBusiness?.id],
    queryFn: () => {
      const id = blockBusiness ? initialBusinessId : selectedBusiness?.id;
      if (id) {
        return businessService.getById(id);
      }
    },
    enabled: blockBusiness ? !!initialBusinessId : !!selectedBusiness?.id,
  });

  const services = useMemo(() => {
    return business?.services?.filter(s => s.isActive) || [];
  }, [business]);

  // Service IDs that have at least one active employee offering them
  const servicesWithEmployees = useMemo(() => {
    const employeeServices = business?.employees?.filter(e => e.isActive)
      .flatMap(e => e.businessEmployeeServices?.map(bes => bes.serviceId) || []) || [];
    return new Set(employeeServices);
  }, [business]);

  // Client search (viaScheduler)
  const { data: clients, isFetching: isFetchingClients } = useQuery({
    queryKey: ['clients', selectedBusiness?.id, clientSearch],
    queryFn: () => clientService.searchClients(selectedBusiness!.id, clientSearch || undefined),
    enabled: !!selectedBusiness && viaScheduler,
  });

  // ── Preencher campos iniciais via props ──────────────────────

  useEffect(() => {
    if (blockBusiness && business) {
      setSelectedBusiness(business);
    }
  }, [business, blockBusiness]);

  // Se há initialServiceId, seleciona o serviço e abre o modal automaticamente
  useEffect(() => {
    if (initialServiceId && services.length > 0) {
      const svc = services.find(s => s.id === initialServiceId);
      if (svc) setSchedulingService(svc);
    }
  }, [services, initialServiceId]);

  // ── Handlers ─────────────────────────────────────────────────

  const handleChangeBusiness = (value?: IBusinessPublicView | IBusinessView) => {
    setSelectedBusiness(value);
    setSchedulingService(null);
  };

  const handleScheduleService = useCallback((service: IServiceView) => {
    setSchedulingService(service);
  }, []);

  const handleScheduleSuccess = useCallback(() => {
    setSchedulingService(null);
    onSuccess();
  }, [onSuccess]);

  const handleScheduleClose = useCallback(() => {
    setSchedulingService(null);
  }, []);

  // Expor estado ao wrapper — delega submit ao ScheduleModal
  const canSubmit = modalApi?.canSubmit ?? false;
  const isLoading = modalApi?.isLoading ?? false;

  const submit = useCallback(() => {
    modalApi?.submit();
  }, [modalApi]);

  useEffect(() => {
    onFormReady?.({ isLoading, canSubmit, submit });
  }, [isLoading, canSubmit, submit, onFormReady]);

  // ── Render ───────────────────────────────────────────────────

  return (
    <Box display='flex' flexDirection='column' gap={2}>
      {/* Business Selection */}
      {blockBusiness && isLoadingBusiness ? (
        <Box display='flex' alignItems='center' gap={2} p={2}>
          <Skeleton variant='rounded' width={56} height={56} sx={{ borderRadius: 2 }} />
          <Box flex={1}>
            <Skeleton variant='text' width='60%' height={28} />
            <Skeleton variant='text' width='40%' height={20} />
          </Box>
        </Box>
      ) : blockBusiness && business ? (
        <Card
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'action.hover',
          }}
        >
          <Avatar
            src={business.profilePicture}
            slotProps={{ img: { loading: 'lazy' } }}
            sx={{ width: 56, height: 56, borderRadius: 2 }}
          />
          <Box flex={1}>
            <Typography variant='subtitle1' fontWeight={700}>
              {business.name}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {business.address}
            </Typography>
          </Box>
        </Card>
      ) : (
        <EntitySelector
          options={businesses?.map(b => ({ id: b.id, name: b.name })) || []}
          value={selectedBusiness?.id}
          onChange={(id) => handleChangeBusiness(id ? businesses?.find(b => b.id === id) : undefined)}
          label='Estabelecimento'
          disabled={blockBusiness}
          dataTestid='select-business'
          loading={isLoadingBusinesses}
        />
      )}

      {/* Client Search (viaScheduler — antes dos serviços) */}
      {viaScheduler && selectedBusiness && (
        <Box display='flex' gap={1} alignItems='flex-start'>
          <Autocomplete
            sx={{ flex: 1 }}
            options={clients || []}
            getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${opt.name}${opt.phone ? ` - ${opt.phone}` : ''}`}
            value={selectedClient}
            onChange={(_, value) => {
              if (value && typeof value !== 'string') {
                setSelectedClient(value);
              } else {
                setSelectedClient(null);
              }
            }}
            onInputChange={(_, value) => {
              setClientSearch(value);
            }}
            freeSolo
            loading={isFetchingClients}
            loadingText='Buscando clientes...'
            noOptionsText='Nenhum cliente encontrado'
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Cliente'
                required
                variant='outlined'
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isFetchingClients ? <LinearProgress sx={{ width: 20 }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <IconButton
            onClick={() => setClientModalOpen(true)}
            color='primary'
            title='Novo cliente'
            sx={{ mt: 0.5 }}
          >
            <PersonAddIcon />
          </IconButton>
        </Box>
      )}

      {/* Service List */}
      {isLoadingBusiness && blockBusiness ? (
        <Box display='flex' flexDirection='column' gap={1.5}>
          <Skeleton variant='text' width={100} height={28} />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant='rounded' height={72} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      ) : services.length > 0 ? (
        <Box display='flex' flexDirection='column' gap={1.5}>

          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Typography variant='subtitle1' fontWeight={600}>
              Serviços
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size='small'
                  checked={showUnavailable}
                  onChange={(e) => setShowUnavailable(e.target.checked)}
                />
              }
              label='Mostrar indisponíveis'
              labelPlacement='start'
              sx={{ mr: 0 }}
            />
          </Box>
          {services
            .filter(s => showUnavailable || servicesWithEmployees.has(s.id))
            .map((service) => {
              const hasEmployee = servicesWithEmployees.has(service.id);
              const clientRequired = viaScheduler && !!selectedBusiness;
              const clientMissing = clientRequired && !selectedClient;
              const isDisabled = !hasEmployee || clientMissing;
              const tooltip = !hasEmployee
                ? 'Nenhum profissional disponível para este serviço no momento.'
                : 'Selecione um cliente antes de agendar.';
              return (
                <ServiceCard
                  key={service.id}
                  service={service}
                  onSchedule={handleScheduleService}
                  disabled={isDisabled}
                  disabledTooltip={tooltip}
                />
              );
            })}
        </Box>
      ) : !isLoadingBusiness && selectedBusiness ? (
        <Typography variant='body2' color='text.secondary' textAlign='center' py={2}>
          Nenhum serviço disponível no momento.
        </Typography>
      ) : null}

      {/* Schedule Modal */}
      {schedulingService && business && (
        <ScheduleModal
          open={!!schedulingService}
          onClose={handleScheduleClose}
          business={business}
          service={schedulingService}
          viaScheduler={viaScheduler}
          walkIn={walkIn}
          blockEmployee={blockEmployee}
          initialEmployeeId={initialBusinessEmployeeId || initialEmployeeId}
          preselectedClient={selectedClient}
          userId={userId}
          onSuccess={handleScheduleSuccess}
          onReady={setModalApi}
        />
      )}

      {/* Create Client Modal */}
      {selectedBusiness && (
        <CreateClientModal
          open={clientModalOpen}
          businessId={selectedBusiness.id}
          onClose={() => setClientModalOpen(false)}
        />
      )}
    </Box>
  );
};
